import {
	AddressType,
	GetItemCounts,
	InfiniteData,
	PaginatedResponseType,
	SendsPageItemType,
	useInfiniteAccountUsers,
	useInfiniteAddresses,
	UserDataType,
} from '@ukpostbox/client-data';
import {
	AlertBanner,
	Button,
	ButtonSubmit,
	Colour,
	ColourStateSolid,
	ComboBoxControl,
	ComboBoxItemDisplayEnum,
	ComboBoxTypeEnum,
	FlexGap,
	FormWrapperLabel,
	StackVertical,
	ToggleSwitch,
	ToggleType,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { GetArrayIndexByKeyValue } from '@ukpostbox/helpers';
import { Controller, FieldValues, FormProvider, useForm } from '@ukpostbox/frontend-framework/lib/ReactHookForm';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useSite } from '../../../_Global/Providers/SiteInterfaceProvider';
import { AddressControl } from '../../../_Global/Components/Address/AddressControl';
import CreateUserModal from './Modals/CreateUserModal';
import { SendsDeliveryOptionsByCountry } from './Controls/SendsDeliveryOptionsByCountry';

type CreateStandaloneSendFormType = {
	saveCallback: any;
	dataUpdateCallback?: any;
	existingData?: SendsPageItemType;
	savingData?: boolean;
	saveResponse?: any;
	error?: any;
};

const CreateStandaloneSendForm = memo((props: CreateStandaloneSendFormType) => {
	const { t } = useTranslation();
	const { currentLocale } = useSite();
	const postageFieldArrayName = 'postageOptions';

	const formMethods = useForm({
		mode: 'onChange',
		defaultValues: {
			title: props.existingData?.title || null,
			description: props.existingData?.description || null,
			allowCreateAddress: props.existingData?.allowCreateAddress || false,
			addresses: props.existingData?.addresses.map((item) => item.id) || [],
			users: props.existingData?.users.map((item) => item.id) || [],
			[postageFieldArrayName]: [],
		},
	});

	const [preSelectedUsers, setPreSelectedUsers] = useState<any>(
		props.existingData?.users.map((item) => item.id) ?? [],
	);
	const [preSelectedAddresses, setPreSelectedAddresses] = useState<any>(
		props.existingData?.addresses.map((item) => item.id) ?? [],
	);

	const {
		status: addressStatus,
		isLoading: addressLoading,
		data: addressData,
		error: addressError,
		dataUpdatedAt: addressDataUpdatedAt,
		fetchNextPage: fetchNextAddressPage,
		hasNextPage: hasNextAddressPage,
		isFetchingNextPage: isFetchingNextAddressPage,
	} = useInfiniteAddresses();

	const {
		isLoading: usersLoading,
		data: usersData,
		error: usersError,
		dataUpdatedAt: usersDataUpdatedAt,
		fetchNextPage: fetchNextUsersPage,
		hasNextPage: hasNextUsersPage,
		isFetchingNextPage: isFetchingNextUsersPage,
		isStale: isStaleUsers,
		isRefetching: isRefetchingUsers,
	} = useInfiniteAccountUsers();

	const [activeSendsPage, setActiveSendsPage] = useState<any>({ isOpen: false, pageData: null });

	const [UserOptions, setUserOptions] = useState<any>([]);

	const {
		watch,
		control,
		register,
		handleSubmit,
		getValues,
		setValue,
		reset,
		formState: { errors, isValid },
	} = formMethods;

	const [createUserOpen, setCreateUserOpen] = useState<boolean>(false);

	const [uniqueCountries, setUniqueCountries] = useState<any>([]);

	const [resetPage, setResetPage] = useState<boolean>(false);

	const [usersItemCounts, setUsersItemCounts] = useState<any>({ loaded: 0, total: 0 });

	const MapUserOptions = (data: InfiniteData<PaginatedResponseType>) => {
		setUsersItemCounts(GetItemCounts(data));

		const newUsers = data?.pages.map(
			(page: any) =>
				page?.data &&
				page.data.map((user: UserDataType, index: number) => ({ label: user.name, value: user.userId })),
		);

		setUserOptions(newUsers.flat());

		if (hasNextUsersPage) {
			fetchNextUsersPage().then((r) => null);
		}
	};

	const ShapeData = useCallback(
		(data: FieldValues, preview?: boolean) => {
			const shapedData: SendsPageItemType = {
				title: data.title,
				description: data.description,
				allowCreateAddress: data.allowCreateAddress ? data.allowCreateAddress : false,
				isActive: true,
				addresses: [],
				users: [],
			};

			let countries: Array<any> = [];

			addressData?.pages.map(
				(page) =>
					page?.data &&
					page.data.forEach((address) => {
						if (data.addresses && data.addresses.includes(address.object.addressId)) {
							countries.push(address);

							if (data[postageFieldArrayName]) {
								const countryCodeMatchIndex = GetArrayIndexByKeyValue(
									data[postageFieldArrayName],
									'label',
									address.object.countryCode,
								);

								if (preview) {
									shapedData.addresses.push({
										id: address.object.addressId,
										value: address.label,
										postageOptions:
											countryCodeMatchIndex > -1
												? data[postageFieldArrayName][countryCodeMatchIndex].value
												: [],
									});
								} else {
									shapedData.addresses.push({
										id: address.object.addressId,
										postageOptions:
											countryCodeMatchIndex > -1
												? data[postageFieldArrayName][countryCodeMatchIndex].value
												: [],
									});
								}
							}
						}
					}),
			);

			if (data.users) {
				data.users.forEach((user: any) => shapedData.users.push({ id: user }));
			}

			countries = Array.from(new Set(countries));

			setUniqueCountries(countries);

			return shapedData;
		},
		[postageFieldArrayName, addressData, uniqueCountries],
	);

	const HandleSave = (data: FieldValues) => {
		props.saveCallback(ShapeData(data));
	};

	const HandleNewUser = (id: number) => {
		const currentData = getValues('users') ?? [];
		const newData: any = [...currentData, id];
		setCreateUserOpen(false);
		setPreSelectedUsers(newData);
		setValue('users', newData);
	};

	const HandleNewAddress = (address: AddressType) => {
		console.log('HandleNewAddress', address);
		const currentData = getValues('addresses') ?? [];
		const newData: any = [...currentData, address.addressId];

		setPreSelectedAddresses(newData);
		setValue('addresses', newData);
	};

	const HandleNewBulkAddresses = (data: Array<AddressType>) => {
		const newIds: Array<number> = [];
		data.forEach((item: AddressType) => newIds.push(item.addressId));

		const currentData = getValues('addresses') ?? [];
		const newData: any = [...currentData, ...newIds];

		setPreSelectedAddresses(newData);
		setValue('addresses', newData);
	};

	const ResetSendsCreation = () => {
		reset({
			title: null,
			description: null,
		});
		setActiveSendsPage({ isOpen: false, pageData: null });
		setResetPage(true);
		setPreSelectedAddresses([]);
		setPreSelectedUsers([]);
		setUniqueCountries([]);
	};

	useEffect(() => {
		console.log(props.saveResponse);
		if (props.saveResponse) {
			ResetSendsCreation();
		}
		// setActiveSendsPage({ isOpen: !!props.saveResponse, pageData: props.saveResponse });
	}, [props.saveResponse]);

	useEffect(() => {
		document.title = props.existingData
			? t('status.editing', { item: props.existingData?.title })
			: t('sends.create.title');
	}, [currentLocale]);

	useEffect(() => {
		if (usersData) {
			MapUserOptions(usersData);
		}
	}, [usersData, usersDataUpdatedAt]);

	useEffect(() => {
		const subscription = watch((e) => {
			if (props.dataUpdateCallback && addressStatus === 'success') {
				props.dataUpdateCallback(ShapeData(e));
			}
		});

		return () => {
			subscription.unsubscribe();
		};
	}, [props, addressStatus, watch]);

	useEffect(() => {
		if (addressStatus === 'success') {
			ShapeData(formMethods.getValues());
		}
	}, [addressStatus]);

	return (
		<>
			<FormProvider {...formMethods}>
				<form onSubmit={handleSubmit((data) => HandleSave(data))}>
					<fieldset className='flex-column flex-gap-md'>
						<Controller
							control={control}
							name='title'
							rules={{
								required: t('validation.required'),
								minLength: { value: 2, message: 'Must be longer than 2 characters' },
								maxLength: { value: 512, message: 'Maximum 512 characters' },
							}}
							render={({ field, fieldState }) => (
								<FormWrapperLabel
									label={t('sends.create.labels.title')}
									invalid={fieldState.invalid}
									validationMessage={fieldState.error?.message}
								>
									<input
										type='text'
										aria-invalid={errors.title ? 'true' : 'false'}
										defaultValue={field.value as string}
										onChange={(e) => field.onChange(e.currentTarget.value)}
									/>
								</FormWrapperLabel>
							)}
						/>

						<Controller
							control={control}
							name='description'
							rules={{
								minLength: { value: 2, message: 'Must be longer than 2 characters' },
								maxLength: { value: 1024, message: 'Maximum 1024 characters' },
							}}
							render={({ field, fieldState }) => (
								<FormWrapperLabel
									label={t('sends.create.labels.description')}
									labelExtra={`(${t('optional')})`}
									invalid={fieldState.invalid}
									validationMessage={fieldState.error?.message}
								>
									<textarea
										rows={5}
										aria-invalid={errors.description ? 'true' : 'false'}
										defaultValue={field.value as string}
										onChange={(e) => field.onChange(e.currentTarget.value)}
									/>
								</FormWrapperLabel>
							)}
						/>

						<div
							className={`${uniqueCountries.length > 0 ? 'p-sm' : ''}`}
							style={{
								borderRadius: 'var(--border-radius)',
								backgroundColor: uniqueCountries.length > 0 ? 'var(--surface-bg)' : '',
							}}
						>
							<Controller
								control={control}
								name='addresses'
								rules={{
									required: t('validation.required'),
								}}
								render={({ field, fieldState }) => (
									<StackVertical gap={FlexGap.ExtraSmall}>
										<FormWrapperLabel
											label={t('sends.create.labels.address')}
											invalid={fieldState.invalid}
											validationMessage={fieldState.error?.message}
										>
											<AddressControl
												name='addresses'
												defaultValues={preSelectedAddresses}
												showCreateAddressOptions
												forwardValuesCallback={(e) => field.onChange(e)}
												forwardFullAddressesCallback={(e) => {
													setUniqueCountries(e);
												}}
												newAddressCallback={(e) => HandleNewAddress(e)}
												bulkAddressCallback={(e) => HandleNewBulkAddresses(e)}
											/>
										</FormWrapperLabel>

										<SendsDeliveryOptionsByCountry
											postageFieldArrayName={postageFieldArrayName}
											existingData={props.existingData}
											uniqueCountries={uniqueCountries}
											reset={resetPage}
										/>
									</StackVertical>
								)}
							/>
						</div>

						<Controller
							control={control}
							name='users'
							rules={{
								required: t('validation.required'),
							}}
							render={({ field, fieldState }) => (
								<FormWrapperLabel
									label={t('sends.create.labels.users')}
									invalid={fieldState.invalid}
									validationMessage={fieldState.error?.message}
									addonEnd={
										<Button
											title={t('sends.create.actions.addUser')}
											disabled={usersLoading}
											onClick={() => setCreateUserOpen(true)}
										/>
									}
								>
									<ComboBoxControl
										name='users'
										placeholder={t('sends.create.controls.usersPlaceholder')}
										data={UserOptions}
										defaultValue={preSelectedUsers}
										loading={usersLoading}
										valueType='string'
										pagination={{
											hasNextPage: hasNextUsersPage,
											itemCounts: usersItemCounts,
											isStale: isStaleUsers,
											isRefetching: isRefetchingUsers,
											isFetchingNextPage: isFetchingNextUsersPage,
											fetchNextPage: fetchNextUsersPage,
										}}
										formReset={resetPage}
										displayType={ComboBoxTypeEnum.Multi}
										displayItemLabelType={ComboBoxItemDisplayEnum.Label}
										forwardValuesCallback={(e) => {
											field.onChange(e);
										}}
									/>
								</FormWrapperLabel>
							)}
						/>

						<Controller
							control={control}
							name='allowCreateAddress'
							defaultValue={props.existingData?.allowCreateAddress ?? false}
							render={({ field, fieldState: { error } }) => (
								<ToggleSwitch
									inForm
									type={ToggleType.Switch}
									defaultChecked={field.value}
									onChange={(e) => field.onChange(e.currentTarget.checked)}
									validationMessage={error?.message}
									label={t('sends.create.labels.usersAddAddress')}
									name='UsersAddAddress'
								/>
							)}
						/>

						{props.error &&
							Object.entries(props.error).map(([k, v], index) => (
								<AlertBanner title={props.error[k].toString()} colour={Colour.Alert} />
							))}

						<ButtonSubmit
							title={t('sends.create.actions.save')}
							colour={ColourStateSolid.Primary}
							fullWidth
							loading={props.savingData}
							disabled={!isValid}
						/>
					</fieldset>
				</form>
			</FormProvider>

			<CreateUserModal
				isOpen={createUserOpen}
				closeCallback={() => setCreateUserOpen(false)}
				actionedCallback={(id: number) => HandleNewUser(id)}
			/>

			{/*            <SendsPageCreatedActionsModal
                pageData={activeSendsPage.pageData}
                isOpen={activeSendsPage.isOpen}
                actionedCallback={() => RestartSendsCreation()}
                closeCallback={() => setActiveSendsPage({...activeSendsPage, isOpen: false})}
            /> */}
		</>
	);
});

export default CreateStandaloneSendForm;
